import { useQuery } from '@tanstack/react-query'
import { api } from '../lib/api'

// ── Helper params builder ─────────────────────────────────────────

interface KpiFilterOpts {
  year?: number | 'cumulative'
  apiDateFrom?: string
  apiDateTo?: string
  customer?: string
  service_line?: string
  buId?: string | null
}

function buildKpiParams(opts: KpiFilterOpts): string {
  const params = new URLSearchParams()
  if (opts.apiDateFrom && opts.apiDateTo) {
    params.set('date_from', opts.apiDateFrom)
    params.set('date_to', opts.apiDateTo)
  } else if (opts.year === 'cumulative') {
    params.set('cumulative', 'true')
  } else if (opts.year !== undefined) {
    params.set('year', String(opts.year))
  }
  if (opts.customer) params.set('customer', opts.customer)
  if (opts.service_line) params.set('service_line', opts.service_line)
  if (opts.buId) params.set('bu_id', opts.buId)
  return params.toString() ? `?${params.toString()}` : ''
}

// ── Anno disponibili ──────────────────────────────────────────────

export function useAvailableYears() {
  return useQuery<number[]>({
    queryKey: ['kpis', 'available-years'],
    queryFn: () => api.get<number[]>('/api/v1/kpis/available-years'),
    staleTime: 5 * 60 * 1000, // 5 minuti
  })
}

// ── Sezione F — Recurring Revenue ─────────────────────────────────

export interface QuarterlyEntry {
  quarter: string   // "2026-Q1"
  amount: number
}

export interface ServiceLineEntry {
  service_line: string
  forecast_total: number
  contract_count: number
}

export interface ContractForecastDetail {
  internal_id: string
  contract_id: string
  customer_name: string
  service_line: string
  recurring_value: number
  recurring_period: number
  period_label: string
  monthly_value: number
  contract_start: string | null
  contract_end: string | null
  auto_renewal: boolean
  residual_months: number
  forecast_total: number
  forecast_year_1: number
  forecast_year_2: number
  confidence: 'high' | 'medium' | 'low'
  days_to_expiry: number | null
}

export interface ConfidenceLevelDetail {
  amount: number
  count: number
}

export interface RecurringRevenueKPIs {
  active_contracts: number
  forecast_total: number
  forecast_year_1: number
  forecast_year_2: number
  monthly_avg: number
  contracts_expiring_30d: number
  contracts_expiring_90d: number
  contracts_expiring_365d: number
  confidence_breakdown: Record<string, ConfidenceLevelDetail>
  quarterly: QuarterlyEntry[]
  by_service_line: ServiceLineEntry[]
  contracts: ContractForecastDetail[]
  computed_at: string
  ref_date: string
  growth_rate_applied: number
}

export function useRecurringRevenueKPIs(opts: { customer?: string; service_line?: string; buId?: string | null } = {}) {
  const params = new URLSearchParams()
  if (opts.customer) params.set('customer', opts.customer)
  if (opts.service_line) params.set('service_line', opts.service_line)
  if (opts.buId) params.set('bu_id', opts.buId)
  const qs = params.toString() ? `?${params.toString()}` : ''

  return useQuery<RecurringRevenueKPIs>({
    queryKey: ['kpis', 'recurring-revenue', opts.customer, opts.service_line, opts.buId],
    queryFn: () => api.get<RecurringRevenueKPIs>(`/api/v1/kpis/recurring-revenue${qs}`),
    staleTime: 60 * 1000,
  })
}

// ── Sezione D — Billing & Collections ───────────────────────────

export interface DsoPoint {
  month: string   // "2025-01"
  dso: number
}

export interface AgingBucket {
  amount: number
  count: number
}

export interface AgingAR {
  '1_30':    AgingBucket
  '31_60':   AgingBucket
  '61_90':   AgingBucket
  '90_plus': AgingBucket
}

export interface CashForecastPoint {
  week: string          // "2025-W01" o "2026-03-24"
  expected_inflow: number
  cumulative: number
}

export interface TopCustomer {
  customer_name: string
  customer_id: string
  outstanding: number
}

export interface BillingKPIs {
  invoiced_ytd: number
  collected_ytd: number
  collection_rate_ytd: number
  invoiced_mtd: number
  collected_mtd: number
  dso_days: number
  dso_days_prev: number | null
  dso_trend: DsoPoint[]
  aging_ar: AgingAR
  total_outstanding: number
  total_overdue: number
  overdue_rate_pct: number
  cash_forecast_13w: CashForecastPoint[]
  cash_forecast_total: number
  top_customers: TopCustomer[]
  top_3_concentration_pct: number
  concentration_alert: boolean
  avg_payment_days: number
  computed_at: string
  coverage_score_pct: number
}

export function useBillingKPIs(opts: { year?: number | 'cumulative'; apiDateFrom?: string; apiDateTo?: string; customer?: string; buId?: string | null } = {}) {
  const qs = buildKpiParams({ year: opts.year, apiDateFrom: opts.apiDateFrom, apiDateTo: opts.apiDateTo, customer: opts.customer, buId: opts.buId })

  return useQuery<BillingKPIs>({
    queryKey: ['kpis', 'billing', opts.year, opts.apiDateFrom, opts.apiDateTo, opts.customer, opts.buId],
    queryFn: () => api.get<BillingKPIs>(`/api/v1/kpis/billing${qs}`),
    staleTime: 60 * 1000,
  })
}

// ── Sezione C — Margin Control ───────────────────────────────────

export interface ProjectMargin {
  order_id: string
  customer_name: string
  service_line: string | null
  order_value: number
  budget_cost: number
  cost_to_date: number
  completion_pct: number
  eac: number           // Estimate at Completion
  margin_pct: number
  burn_rate_pct: number // cost_to_date / budget_cost * 100
  hours_worked: number
  status: string
}

export interface MarginKPIs {
  avg_margin_pct: number
  total_profitability: number
  projects_at_risk: number    // margine < soglia (default 15%)
  projects_in_loss: number    // margine negativo
  total_hours_customer: number
  total_hours_rd: number
  utilization_pct: number | null
  projects: ProjectMargin[]
  computed_at: string
  coverage_score_pct: number
}

export function useMarginKPIs(opts: { year?: number | 'cumulative'; apiDateFrom?: string; apiDateTo?: string; customer?: string; service_line?: string; buId?: string | null } = {}) {
  const qs = buildKpiParams(opts)

  return useQuery<MarginKPIs>({
    queryKey: ['kpis', 'margin', opts.year, opts.apiDateFrom, opts.apiDateTo, opts.customer, opts.service_line, opts.buId],
    queryFn: () => api.get<MarginKPIs>(`/api/v1/kpis/margin${qs}`),
    staleTime: 60 * 1000,
  })
}

// ── Sezione B — Revenue Operations ───────────────────────────────

export interface MonthlyTrendPoint {
  month: string    // "2025-01"
  confirmed: number
  recognized: number
}

export interface BacklogAging {
  bucket_0_30: number
  bucket_31_90: number
  bucket_90_plus: number
}

export interface RevenueOpsKPIs {
  confirmed_orders: number
  probable_orders: number
  pipeline_coverage_pct: number
  target_coverage_pct: number
  win_rate_pct: number | null
  recognition_rate_pct: number
  gap_orders_recognized: number
  avg_completion_pct: number
  monthly_trend: MonthlyTrendPoint[]
  backlog_aging: BacklogAging
  computed_at: string
  coverage_score_pct: number
}

export function useRevenueOpsKPIs(opts: { year?: number | 'cumulative'; apiDateFrom?: string; apiDateTo?: string; customer?: string; service_line?: string; buId?: string | null } = {}) {
  const qs = buildKpiParams(opts)

  return useQuery<RevenueOpsKPIs>({
    queryKey: ['kpis', 'revenue-ops', opts.year, opts.apiDateFrom, opts.apiDateTo, opts.customer, opts.service_line, opts.buId],
    queryFn: () => api.get<RevenueOpsKPIs>(`/api/v1/kpis/revenue-ops${qs}`),
    staleTime: 60 * 1000,
  })
}

// ── Sezione A — Executive Cockpit ────────────────────────────────

export interface OrderBreakdownItem {
  name: string
  value: number
  pct: number
}

export interface ExecutiveKPIs {
  revenue_ytd: number
  revenue_mtd: number
  revenue_vs_budget_pct: number
  gross_margin_pct: number
  dso_days: number
  overdue_amount: number
  overdue_pct: number
  backlog_amount: number
  active_projects: number
  revenue_ytd_prev?: number
  dso_days_prev?: number
  revenue_by_month?: Array<{ month: number; current: number; previous?: number | null }>
  orders_by_customer?: OrderBreakdownItem[]
  orders_by_service_line?: OrderBreakdownItem[]
  computed_at: string
  data_freshness_hours: number
  coverage_score_pct: number
  // SB.1 — KPI avanzati
  ebitda?: number
  opex_ytd?: number
  cash_runway_months?: number | null
  cash_not_configured?: boolean
  // CE.1 — costi aziendali
  cost_entries_actual?: number
  // SB.4 — NRR, Churn, LTV, Burn rate
  nrr_pct?: number | null
  churn_rate_pct?: number | null
  churn_insufficient_data?: boolean
  ltv?: number | null
  burn_rate_monthly?: number
}

export function useExecutiveKPIs(opts: { year?: number | 'cumulative'; apiDateFrom?: string; apiDateTo?: string; customer?: string; service_line?: string; buId?: string | null } = {}) {
  const qs = buildKpiParams(opts)

  return useQuery<ExecutiveKPIs>({
    queryKey: ['kpis', 'executive', opts.year, opts.apiDateFrom, opts.apiDateTo, opts.customer, opts.service_line, opts.buId],
    queryFn: () => api.get<ExecutiveKPIs>(`/api/v1/kpis/executive${qs}`),
    staleTime: 60 * 1000,
  })
}

// ── BU breakdown e performance ─────────────────────────────────────

export interface BUBreakdownItem {
  bu_id: string | null
  bu_name: string
  bu_color: string
  value: number
  target: number | null
  delta_pct: number | null
}

export function useBUBreakdown(opts: {
  metric: 'revenue' | 'margin' | 'hours'
  period: 'ytd' | 'year'
  year?: number
}) {
  const params = new URLSearchParams({ metric: opts.metric, period: opts.period })
  if (opts.year) params.set('year', String(opts.year))

  return useQuery<BUBreakdownItem[]>({
    queryKey: ['kpis', 'bu-breakdown', opts.metric, opts.period, opts.year],
    queryFn: () => api.get<BUBreakdownItem[]>(`/api/v1/kpis/bu-breakdown?${params.toString()}`),
    staleTime: 60 * 1000,
  })
}

export interface BUPerformance {
  bu_id: string
  bu_name: string
  bu_code: string
  bu_color: string
  year: number
  revenue_ytd: number
  margin_ytd: number
  margin_pct: number
  hours_ytd: number
  target_revenue: number | null
  target_margin: number | null
  revenue_delta_pct: number | null
  margin_delta_pct: number | null
}

export function useBUPerformance(buId: string | null, year?: number) {
  const qs = year ? `?year=${year}` : ''
  return useQuery<BUPerformance>({
    queryKey: ['bu-performance', buId, year],
    queryFn: () => api.get<BUPerformance>(`/api/v1/business-units/${buId}/performance${qs}`),
    enabled: !!buId,
    staleTime: 60 * 1000,
  })
}
